// src/screens/DataHealth.js
// ADM-DH-01 (May 15, 2026) — live data coverage scorecard.
// Previously sourced from a static src/data/dataAudit.js file. Now reads
// directly from public.\"Locations\" via the admin_data_health() RPC for live
// numbers that update with every venue add or backfill.

import React, { useState, useEffect } from 'react';
import { Card, CardTitle, Grid, MetricCard, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,\"Helvetica Neue\",Arial,sans-serif';

function CoverageBar({ pct }) {
  const color = pct >= 90 ? '#5d8a5e' : pct >= 70 ? '#c4a86b' : '#9c4a3a';
  return (
    <div style={{ height: 8, borderRadius: 4, background: '#edf2f5', overflow: 'hidden', marginTop: 6 }}>
      <div style={{ width: pct + '%', height: '100%', background: color, transition: 'width .2s' }} />
    </div>
  );
}

function CoverageRow({ label, covered, total, hint }) {
  const pct = total > 0 ? Math.round((covered / total) * 1000) / 10 : 0;
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #eef2f5' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontFamily: F, fontSize: 14, color: '#0f2a40', fontWeight: 600 }}>{label}</div>
        <div style={{ fontFamily: F, fontSize: 13, color: '#4e7f97' }}>
          <b style={{ color: '#0f2a40' }}>{covered.toLocaleString()}</b> / {total.toLocaleString()}
          <span style={{ color: '#a6bcc8' }}> · {pct}%</span>
        </div>
      </div>
      {hint ? <div style={{ fontFamily: F, fontSize: 12, color: '#7a8e98', marginTop: 2 }}>{hint}</div> : null}
      <CoverageBar pct={pct} />
    </div>
  );
}

export default function DataHealth() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    supabase.rpc('admin_data_health').then(({ data, error }) => {
      if (cancelled) return;
      if (error) { setError(error.message); return; }
      setD(data);
    });
    return () => { cancelled = true; };
  }, []);

  if (error) return <Insight type='warn'>{error}</Insight>;
  if (!d) return <Insight type='info'>Loading…</Insight>;

  return (
    <div style={{ fontFamily: F }}>
      <h2 style={{ fontSize: '1.05rem', margin: '0 0 4px', fontWeight: 600 }}>Data Health</h2>
      <p style={{ fontSize: '.72rem', color: '#6F8FA7', marginBottom: 14 }}>
        Live coverage across the {d.total_locations.toLocaleString()} venues in the directory. Pull updates whenever you backfill.
      </p>

      <Grid cols={4}>
        <MetricCard label='Total venues' value={d.total_locations.toLocaleString()} sub={d.cities + ' cities · ' + d.countries + ' countries'} accent='#314F5C' />
        <MetricCard label='Curated' value={d.curated.toLocaleString()} sub={(d.total_locations ? Math.round(100*d.curated/d.total_locations) : 0) + '%'} accent='#9F7E4F' />
        <MetricCard label='With phone' value={d.with_phone.toLocaleString()} sub={(d.total_locations ? Math.round(100*d.with_phone/d.total_locations) : 0) + '%'} accent='#28a745' />
        <MetricCard label='With photos' value={d.with_photos.toLocaleString()} sub={(d.total_locations ? Math.round(100*d.with_photos/d.total_locations) : 0) + '%'} accent='#6F8FA7' />
      </Grid>

      <Card>
        <CardTitle>Coverage breakdown</CardTitle>
        <CoverageRow label='GPS coordinates' covered={d.with_coords} total={d.total_locations} hint='lat/lng filled' />
        <CoverageRow label='Phone' covered={d.with_phone} total={d.total_locations} hint='callable from the app' />
        <CoverageRow label='Website' covered={d.with_website} total={d.total_locations} hint='website URL on file' />
        <CoverageRow label='Email' covered={d.with_email} total={d.total_locations} hint='enables booking auto-send (currently 0%, awaiting research sprint)' />
        <CoverageRow label='Photos' covered={d.with_photos} total={d.total_locations} hint='hero image; missing = empty card' />
        <CoverageRow label='Verified (90d)' covered={d.verified_90d} total={d.total_locations} hint='last_verified within 90 days' />
        <CoverageRow label='Has tags' covered={d.with_tags} total={d.total_locations} hint='categorization for search' />
      </Card>

      {d.top_gaps && d.top_gaps.length > 0 && (
        <Card>
          <CardTitle>Top 10 gappy countries (by missing data)</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.73rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e9eef2' }}>
                <th style={{ textAlign: 'left',  padding: '6px 8px', color: '#6F8FA7' }}>Country</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6F8FA7' }}>Total</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6F8FA7' }}>Missing photos</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6F8FA7' }}>Missing phone</th>
              </tr>
            </thead>
            <tbody>
              {d.top_gaps.map(g => (
                <tr key={g.country} style={{ borderBottom: '.5px solid #f0f2f4' }}>
                  <td style={{ padding: '6px 8px', color: '#0f1e28' }}>{g.country}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{g.total.toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#9F7E4F' }}>{g.missing_photos.toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#dc3545' }}>{g.missing_phone.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
